<template>
  <div class="wrapper" :class="{ lock: lockScrollValue }">
    <!-- Интро при запуске нативного приложения -->
    <AppIntroVideo />
    <!-- Offline баннер (показывается поверх всего) -->
    <OfflineBanner />
    <!-- Полноэкранная offline страница -->
    <OfflineFullPage v-model="showOfflinePage" />
    <hd v-if="!showOfflinePage" @lock-scroll="lockScroll" />
    <main v-if="!showOfflinePage" :class="mainClass" :style="mainStyle">
      <AppUpdateBanner />
      <ClosedRestaurantGlobalBanner />
      <WorkloadBlockedBanner />
      <PaymentPausedBanner />
      <slot></slot>
    </main>
    <!-- Футер рендерится всегда, а на мобильных прячется CSS-ом (max-lg:hidden).
         Раньше он висел на JS-условии width > 1024: на сервере width = 0, футера
         в SSR-разметке не было вовсе — а это единственные внутренние ссылки на
         /policy и /privacy. Страницы были в sitemap, но без единой ссылки на них. -->
    <TheFooter v-if="!showOfflinePage" class="w-full max-lg:hidden"></TheFooter>
    <!-- Баннер согласия — только в вебе, включая мобильный браузер.
         Раньше условием было width > 1024, то есть баннер не показывался и на
         телефонах, где Метрика работает точно так же.
         В приложении баннера нет намеренно: Яндекс.Метрика в мобильной сборке
         выключена (нет ymCounterIdMobile), а своя аналитика — первая сторона.
         Отключить её можно тумблером «Аналитика» в разделе «Безопасность». -->
    <TheCookieModal v-if="!showOfflinePage && !isNativeApp" />
    <DynamicModal v-if="storeModal?.isOpen" @close="storeModal?.close" />

    <!-- Стартовый промпт «Укажите адрес для заказа» (Dodo-стайл нижняя шторка) -->
    <DeliveryAddressPrompt />

    <!-- Стартовая шторка «Выберите филиал»: при каждом запуске, пока нет свежего выбора -->
    <BranchSelectPrompt />

    <!-- Тосты клиентской части (UI-kit), показываются снизу -->
    <UiKitToaster placement="bottom-center" />
  </div>
</template>

<script setup lang="ts">
import consola from 'consola'

import { computed, ref, watch, onMounted, onBeforeUnmount } from 'vue';
import { Capacitor } from '@capacitor/core';
import { useModalStore , useAppReadiness } from '@shared/model';
import {
  useInstitutionStore,
  getReadyInstitutionId,
  useInstitutionAutoRefresh,
  restorePersistedBranchId,
  buildRestaurantGraph,
  graphToJson,
} from '@entities/institution';
import { useCartStore } from '@entities/order';
import { useUserProfileStore } from '@entities/user';
import { useDeepLinkRedirect , useAuthStore } from '@features/auth';
import { useDeliveryAddressPrompt, DeliveryAddressPrompt } from '@features/delivery-address-prompt';
import { useBranchSelectPrompt, BranchSelectPrompt } from '@features/branch-select-prompt';


import TheCookieModal from '~/widgets/the-cookie-modal/ui/TheCookieModal.vue';
import APIAdminNotification from '~/fsd-pages/admin/api';
import { useSafeArea, useGetWidth, useAnalytics, setAnalyticsAttribution } from '~/shared/lib';
const _logger = consola.withTag('layout-default');
const { track } = useAnalytics();
const { width } = useGetWidth();
const storeModal = useModalStore();
const storeUserProfile = useUserProfileStore();
const storeInstitution = useInstitutionStore();
const cartStore = useCartStore();
useAuthStore();
const { setReady } = useAppReadiness();

// Стартовый промпт выбора адреса доставки: вешаем вотчер на авторизацию здесь, в setup
// (immediate — отработает и для уже вошедшего юзера, и при логине по ходу сессии).
// При наличии дефолтного адреса он применяется молча; иначе через паузу всплывает шторка.
useDeliveryAddressPrompt().start();

// Стартовая шторка выбора филиала: показывается при каждом запуске приложения,
// пока у пользователя нет свежего (моложе суток) сохранённого выбора филиала.
useBranchSelectPrompt().start();

// Авто-обновление данных заведения: поллинг раз в 60s + refetch при возврате фокуса
// Источник истины — id заведения из стора (асинхронно появляется после init)
useInstitutionAutoRefresh(() => (storeInstitution.institution as { id?: number } | null)?.id ?? null);

// Safe area для адаптации под iOS и Android
const { safeTop, contentPaddingTop } = useSafeArea();

const route = useRoute();

const mainClass = computed(() => 'page');

// Динамический padding с учетом safe area (iOS/Android)
const mainPaddingStyle = computed(() => {
  const isHomePage = route.path === '/' || route.path === '/favorites';
  const isLkPage = route.path.startsWith('/lk'); // Проверяем все страницы раздела /lk
  const isStaticPage = route.path === '/privacy' || route.path === '/policy'; // Статические страницы с legal контентом
  const currentWidth = width.value;

  // Для мобильных используем динамический padding с safe area
  if (currentWidth <= 768) {
    consola.log('safeTop-=', safeTop.value);

    // Для всех страниц раздела /lk не применяем padding-top и safe area
    if (isLkPage) {
      return {
        paddingTop: '0px', // Убираем все отступы safe area для страниц /lk
      };
    }

    // Для статических страниц (/privacy, /policy) НЕ добавляем дополнительный padding
    // Они уже имеют safe area от body в global.scss - избегаем дублирования
    if (isStaticPage) {
      return {
        paddingTop: '0px', // НЕ дублируем safe area
      };
    }

    return {
      paddingTop: isHomePage ? contentPaddingTop.value : `${safeTop.value}px`,
    };
  }

  // Для десктопа используем фиксированные значения
  // Для всех страниц раздела /lk не применяем padding-top на всех разрешениях
  if (isLkPage) {
    return { paddingTop: '0px' };
  }

  // Для статических страниц на десктопе тоже не дублируем
  if (isStaticPage) {
    return { paddingTop: '0px' };
  }

  if (currentWidth >= 1536) {
    return { paddingTop: isHomePage ? '90px' : '112px' };
  }

  if (currentWidth >= 1024) {
    return { paddingTop: isHomePage ? '135px' : '112px' };
  }

  if (currentWidth >= 768) {
    return { paddingTop: isHomePage ? '110px' : '0px' };
  }

  return {};
});

/**
 * Тот же отступ, но ещё и переменной.
 *
 * Отступ сверху резервирует место под фиксированной шапкой — и ровно из-за него обложка
 * «во всю ширину экрана» начиналась не от верха страницы, а от нижнего края шапки:
 * над баннером оставалась полоса фона. Вытащить себя из-под этого отступа обложка может
 * только отрицательным margin'ом, а для этого ей нужно знать величину — число считается
 * здесь, по ширине экрана и маршруту, и в CSS его иначе не достать.
 */
const mainStyle = computed(() => {
  const paddingTop = (mainPaddingStyle.value as { paddingTop?: string }).paddingTop ?? '0px';
  return { ...mainPaddingStyle.value, '--app-main-pt': paddingTop };
});

const faviconUrl = computed(() => storeInstitution.institution?.visual?.favicon_file || '/favicon.ico');

const head = useLocaleHead({
  addDirAttribute: true,
  addSeoAttributes: true,
});

// SEO мета-теги из стора institution
const seoMeta = computed(() => storeInstitution.institution?.seo_meta ?? {});

// Канонический URL (SSR-safe): origin из запроса на сервере, из window на клиенте.
//
// xForwardedHost обязателен: приложение живёт за реверс-прокси, и без флага h3
// берёт заголовок Host — то есть внутренний адрес контейнера. В canonical и og:url
// уезжал бы 127.0.0.1:3000 вместо домена. Серверные маршруты (robots.txt,
// sitemap.xml) читают URL с теми же опциями — значения обязаны совпадать.
const reqUrl = useRequestURL({ xForwardedHost: true, xForwardedProto: true });
const canonicalUrl = computed(() => `${reqUrl.origin}${route.path}`);

// Заголовок страницы: seo_meta.title → имя заведения → дефолт.
// Имя заведения приходит при SSR-prefetch, поэтому title не пустой даже без seo_meta.
const pageTitle = computed(
  () => seoMeta.value?.title || storeInstitution.institution?.name || 'Доставка еды',
);

/**
 * Меню в разметке — только там, где оно на странице и есть.
 *
 * Полный Menu на сотню позиций весит десятки килобайт, и раньше он ехал в
 * каждой странице сайта, включая пользовательское соглашение. Страницам без
 * каталога он не даёт ничего: расширенный сниппет с блюдами строится по
 * главной и по разделам меню.
 */
const withMenu = computed(() => route.path === '/' || route.path.startsWith('/menu/'));

/**
 * schema.org: сайт, заведение, филиалы — одним связанным графом.
 * Сборка вынесена в entities/institution/lib/structuredData.
 */
const jsonLd = computed(() =>
  graphToJson(
    buildRestaurantGraph({
      origin: reqUrl.origin,
      institution: storeInstitution.institution,
      selectedBranch: storeInstitution.selectedBranch,
      branches: storeInstitution.branches ?? [],
      categories: storeInstitution.products ?? [],
      image: ogImage.value || storeInstitution.institution?.visual?.favicon_file,
      cuisine: seoMeta.value?.cuisine,
      withMenu: withMenu.value,
    }),
  ),
);

/**
 * og:type из админки пропускаем только из белого списка.
 *
 * В поле легко записать «restaurant» — такого типа в Open Graph нет (вертикаль
 * ресторанов называется restaurant.restaurant и требует своих обязательных
 * полей), и соцсети молча откатываются на website. Лучше отдать website сразу.
 */
const OG_TYPES = new Set(['website', 'article', 'profile', 'business.business', 'restaurant.restaurant']);

const ogType = computed(() => {
  // Настройка из админки одна на весь сайт, поэтому применяем её только к главной:
  // «пользовательское соглашение» с типом restaurant.restaurant — не то, что имел
  // в виду администратор, заполняя поле.
  if (route.path !== '/') return 'website';

  const configured = (seoMeta.value?.og_type || '').trim();
  if (configured === 'restaurant') return 'restaurant.restaurant';
  return OG_TYPES.has(configured) ? configured : 'website';
});

/**
 * Картинка для соцсетей: настройка SEO → логотип заведения.
 *
 * trim обязателен: адрес приходит из текстового поля админки, и вставленная из
 * буфера ссылка регулярно приезжает с ведущим табом или переносом строки. Для
 * краулера такой URL просто невалиден — превью не собирается вообще.
 */
const ogImage = computed(
  () => (seoMeta.value?.og_image || storeInstitution.institution?.visual?.logo_file || '').trim(),
);

// Формируем SEO мета-теги для глобального использования
const seoMetaTags = computed(() => {
  const seo = seoMeta.value;
  const institutionName = storeInstitution.institution?.name || '';

  // SSR-safe: на сервере origin берётся из запроса (правильный домен для каждого
  // деплоя), на клиенте — из window. Без query-параметров.
  const currentUrl = canonicalUrl.value;

  const meta: Record<string, string>[] = [];

  // robots объявляется в app.vue — он один на все лейауты, включая админские.

  if (seo?.description) {
    meta.push({
      name: 'description',
      content: seo.description,
    });
  }

  if (seo?.keywords) {
    meta.push({
      name: 'keywords',
      content: seo.keywords,
    });
  }

  if (seo?.author) {
    meta.push({
      name: 'author',
      content: seo.author,
    });
  }

  // Open Graph мета-теги
  const ogTitle = seo?.og_title || seo?.title || institutionName;
  const ogDescription = seo?.og_description || seo?.description || '';

  if (institutionName) {
    meta.push({ property: 'og:site_name', content: institutionName });
  }

  meta.push({ property: 'og:locale', content: 'ru_RU' }, { property: 'og:type', content: ogType.value }, { property: 'og:url', content: currentUrl });

  if (ogTitle) meta.push({ property: 'og:title', content: ogTitle });
  if (ogDescription) meta.push({ property: 'og:description', content: ogDescription });
  if (ogImage.value) meta.push({ property: 'og:image', content: ogImage.value });

  // Twitter Card: без неё ссылка в Twitter/X и части мессенджеров разворачивается
  // в текстовую строку без картинки.
  meta.push({ name: 'twitter:card', content: ogImage.value ? 'summary_large_image' : 'summary' });
  if (ogTitle) meta.push({ name: 'twitter:title', content: ogTitle });
  if (ogDescription) meta.push({ name: 'twitter:description', content: ogDescription });
  if (ogImage.value) meta.push({ name: 'twitter:image', content: ogImage.value });

  return meta;
});

/**
 * Подтверждение прав на домен в Яндекс.Вебмастере. Код задаётся в админке
 * (Настройки → SEO) и приезжает вместе с данными заведения, поэтому у каждого бренда
 * он свой без правки кода.
 *
 * Отдельным useHead, а не внутри seoMetaTags: тот возвращает пустой массив, пока не
 * заполнены title и description, — и код верификации молча пропал бы у заведения,
 * которое SEO-тексты ещё не заполнило. Права на сайт от этого не должны зависеть.
 *
 * Тег обязан быть в SSR-выдаче: робот Яндекса читает исходный HTML и JS не исполняет.
 * Данные заведения на сервере уже есть — их подгружает плагин institution-ssr.
 */
useHead(() => {
  const code = seoMeta.value?.yandex_verification;

  return {
    meta: code
      ? [{ key: 'yandex-verification', name: 'yandex-verification', content: code }]
      : [],
  };
});

useHead({
  htmlAttrs: {
    lang: head.value.htmlAttrs?.lang,
    dir: head.value.htmlAttrs?.dir,
  },
  title: () => pageTitle.value,
  meta: () => seoMetaTags.value,
  link: [
    {
      rel: 'canonical',
      href: () => canonicalUrl.value,
    },
    {
      rel: 'icon',
      type: 'image/x-icon',
      href: () => faviconUrl.value,
    },
  ],
  // Сторонних скриптов в <head> нет намеренно. Здесь висели Fancybox, Yandex Maps
  // и VK ID SDK — три чужих бандла на каждой странице сайта. Fancybox не
  // используется в коде вообще, карта нужна только в модалке адреса и админской
  // карте зон, VK ID — только в виджете входа: оба грузятся по требованию,
  // см. shared/lib/platform (loadScriptOnce).
});

// JSON-LD (schema.org Restaurant) — отдельным реактивным useHead, наполняется
// после SSR-prefetch данных заведения.
useHead(() => ({
  script: jsonLd.value
    ? [{ type: 'application/ld+json', innerHTML: jsonLd.value, key: 'ld-json-restaurant' }]
    : [],
}));

// ---- Реактивная подмена favicon на клиенте ----
if (process.client) {
  function setFavicon(url) {
    let link = document.querySelector("link[rel*='icon']");
    if (!link) {
      link = document.createElement('link');
      link.type = 'image/x-icon';
      link.rel = 'icon';
      document.head.append(link);
    }
    link.href = url;
  }

  watch(
    faviconUrl,
    (newUrl) => {
      setFavicon(newUrl);
    },
    { immediate: true },
  );

  // Отладка SEO данных
  watch(
    () => storeInstitution.institution?.seo_meta,
    (newSeoMeta) => {
      consola.log('📊 SEO Meta updated:', newSeoMeta);
    },
    { immediate: true, deep: true },
  );
}
// -----------------------------------------------

const lockScrollValue = ref(false);
const lockScroll = (value) => {
  lockScrollValue.value = value.value;
};

// ✅ Offline режим
const showOfflinePage = ref(false);

// Нативная сборка: в ней баннер согласия на cookie не показывается — см. шаблон.
// Считается один раз: платформа за время жизни приложения не меняется.
const isNativeApp = Capacitor.isNativePlatform();

// Инициализация данных
const init = async () => {
  try {
    consola.log('🚀 Starting app initialization...');

    // Восстановление выбранного филиала после SSR-гидрации.
    // На сервере нет браузерных хранилищ, поэтому SSR-плагин кладёт в payload филиал
    // по умолчанию (первый активный), и гидрация Pinia затирает им значение,
    // прочитанное из хранилищ в инициализаторе рефа. Без этого после
    // каждого рефреша выбор пользователя сбрасывался на первый активный филиал.
    // Приоритет: явный выбор пользователя (localStorage, сутки — branchChoice),
    // затем состояние вкладки (sessionStorage). Вотчеры selectedBranchId
    // (перезагрузка меню) и cart (синхронизация корзины филиала) подхватят смену.
    if (process.client && storeInstitution.branches?.length) {
      // Единая цепочка восстановления (branchChoice → sessionStorage) с проверкой,
      // что филиал существует и активен; протухший явный выбор при этом чистится,
      // чтобы стартовая шторка спросила локацию заново
      const savedBranchId = restorePersistedBranchId(
        id => storeInstitution.branches.some(b => b.id === id && b.is_active !== false),
      );
      if (savedBranchId && savedBranchId !== storeInstitution.selectedBranchId) {
        consola.log('🏪 Restoring persisted branch after hydration:', savedBranchId);
        storeInstitution.setSelectedBranchId(savedBranchId);
      }
    }

    // На website-сборке филиалы уже загружены SSR-плагином (plugins/institution-ssr.ts)
    // и пришли в payload. SSR-снимок мог устареть (кэш заведения до 1ч): админ мог
    // изменить настройки (лояльность/платёжки/филиалы) после серверного рендера.
    // Поэтому на клиентском маунте делаем ФОНОВЫЙ рефетч — не блокируем первую
    // отрисовку (SSR-данные уже на экране), но настройки становятся актуальны сразу.
    // Дальше idle-вкладку догоняет поллер useInstitutionAutoRefresh.
    if (storeInstitution.branches?.length) {
      consola.log('✅ Branches from SSR payload');
      getReadyInstitutionId()
        .then((id) => storeInstitution.fetchBranches(id))
        .then(() => consola.log('✅ Institution refreshed on mount'))
        .catch((error) => consola.warn('institution mount refresh failed:', error));
    } else {
      const institutionID = await getReadyInstitutionId();
      await storeInstitution.fetchBranches(institutionID);
      consola.log('✅ Branches loaded');
    }

    // НЕ отмечаем products как готовые здесь!
    // Products загружаются асинхронно через watcher на selectedCity
    // Будем отслеживать их готовность через watcher ниже

    await storeUserProfile.fetchUser();
    consola.log('✅ User profile loaded');
    setReady('user', true);

    await cartStore.initializeCart();
    consola.log('✅ Cart initialized');
    setReady('cart', true);

    consola.log('✅ App initialization complete');
  } catch (error) {
    consola.error('❌ Initialization error:', error);
  }
};

// Watcher для отслеживания готовности продуктов
// Products загружаются асинхронно через watcher в institution store
watch(
  () => storeInstitution.products,
  (newProducts) => {
    if (newProducts && newProducts.length > 0) {
      consola.log('✅ Products loaded:', {
        categoriesCount: newProducts.length,
        totalProducts: newProducts.reduce((sum, cat) => sum + cat.products.length, 0),
      });
      setReady('products', true);
    }
  },
  { immediate: true, deep: false },
);

/**
 * Слушатели push-уведомлений, добавленные ИМЕННО этим лейаутом.
 * Храним хэндлы, чтобы при размонтировании снять только их и не задеть слушатели
 * push-плагинов, живущие всё время работы приложения.
 */
const pushListeners: Array<{ remove?: () => Promise<void> | void }> = [];

// Инициализация в onMounted для лучшей совместимости с мобильными приложениями
onMounted(async () => {
  // Отмечаем что router готов (он доступен в onMounted)
  setReady('router', true);
  consola.log('✅ Router is ready');

  // ⚠️ ОТКЛЮЧЕНО: Попытка восстановления из SecureStorage
  // SecureStorage плагин (capacitor-secure-storage-plugin v0.11.0) не работает на Android
  // localStorage восстанавливается автоматически при загрузке useSessionManager

  // Запускаем инициализацию
  init();

  // Push notification listeners только для нативных платформ
  if (Capacitor.isNativePlatform()) {
    const { PushNotifications } = await import('@capacitor/push-notifications');

    // Обработчик получения уведомления (foreground)
    const receivedListener = await PushNotifications.addListener('pushNotificationReceived', async (payload) => {
      consola.log('[PUSH] 📥 Notification received:', payload.title);

      const notificationId = payload.data?.notificationId;
      const broadcastId = payload.data?.broadcastId;

      // Событие шлём всегда, даже без идентификаторов: доля пушей, которые вообще
      // доходят до открытого приложения, — сама по себе метрика.
      track('push_received', {
        notificationId: notificationId || broadcastId || undefined,
        campaignId: payload.data?.campaignId ? Number(payload.data.campaignId) : undefined,
        // Этот слушатель срабатывает только при открытом приложении: доставку в фоне
        // видно лишь через Notification Service Extension на iOS и data-only на Android.
        foreground: true,
      });

      if (notificationId || broadcastId) {
        try {
          await APIAdminNotification.markDelivered({ notificationId, broadcastId });
          consola.log('[PUSH] ✅ Marked as delivered:', notificationId || broadcastId);
        } catch (error) {
          consola.error('[PUSH] ❌ Error marking as delivered:', error);
        }
      }
    });

    // Обработчик нажатия на уведомление.
    // Единственная точка обработки тапа на обеих платформах (в iOS-плагине такой же
    // слушатель был удалён — из-за него действие выполнялось дважды).
    const actionListener = await PushNotifications.addListener('pushNotificationActionPerformed', async (action) => {
      const notificationData = action.notification?.data || {};

      consola.log('[PUSH] 🔔 Notification action performed:', {
        actionId: action.actionId,
        data: notificationData,
      });

      const pushId = notificationData.notificationId || notificationData.broadcastId;

      track('push_opened', {
        notificationId: pushId || undefined,
        campaignId: notificationData.campaignId ? Number(notificationData.campaignId) : undefined,
        actionCode: notificationData.actionCode || notificationData.action,
      });

      // Прилипает ко всем событиям ближайших суток, поэтому по рассылке видно не
      // «сколько открыли», а сколько заказов и денег она принесла.
      setAnalyticsAttribution({
        source: 'push',
        notificationId: pushId || undefined,
        campaign: notificationData.campaignId ? String(notificationData.campaignId) : undefined,
      });

      try {
        // Используем универсальный обработчик действий
        // Он сам проверит готовность приложения и поставит в очередь если нужно
        const { useNotificationActionHandler } = await import('@features/notifications');
        const { handleNotificationAction } = useNotificationActionHandler();
        await handleNotificationAction(notificationData);
      } catch (error) {
        consola.error('[PUSH] ❌ Error handling notification action:', error);
      }
    });

    pushListeners.push(receivedListener, actionListener);
  } else {
    consola.log('🔔 [LAYOUT] Push notification listeners skipped - web platform');
  }
});

useDeepLinkRedirect();

onBeforeUnmount(async () => {
  // Снимаем ТОЛЬКО свои слушатели. Раньше здесь был removeAllListeners(), который
  // убивал и слушатели push-плагинов (registration / registrationError) — а они
  // регистрируются один раз при старте приложения. После первого же ухода с этого
  // лейаута (например, в админку) приложение переставало обновлять FCM-токен
  // до полного перезапуска.
  const listeners = pushListeners.splice(0);
  await Promise.all(
    listeners.map(listener =>
      Promise.resolve(listener?.remove?.()).catch((error: unknown) =>
        consola.error('[PUSH] ❌ Error removing listener:', error),
      ),
    ),
  );
});
</script>

<style lang="scss" scoped>
.wrapper {
  @apply flex max-md:overflow-hidden min-h-screen flex-col;

  > main {
    @apply flex-auto pb-24;
  }

  &.lock {
    @apply h-screen;
  }
}
</style>
